import { AxiosInstance } from 'axios';
import {
  positionOpenerAxiosApi,
  resolveOpenerAxios
} from 'src/Config/axiosApi';

/**
 * Per-group opener rule activation — base-URL routing.
 *
 * The opener execution plane is sharded per opener-group: a grouped rule must
 * be activated/paused/toggled against its OWNING group's feed pod, not the
 * always-on core `position-opener-node`. The core pod 409s any write on a
 * grouped pair (so the FE MUST route grouped writes to the group endpoint),
 * and a feed pod 404s another group's rule. Ungrouped pairs (or a missing
 * `VITE_OPENER_FEED_URL` env) fall back to the core base.
 *
 * Endpoint shapes:
 *   grouped:   `${VITE_OPENER_FEED_URL}/groups/{openerGroupId}/api/position-opener`
 *   ungrouped: `${VITE_POSITION_OPENER_URL}/api/position-opener`  (core base)
 */

/** The core (ungrouped) opener API base, including the `/api/position-opener`
 * path. Mirrors the `positionOpenerAxiosApi` baseURL so reads/bulk/ungrouped
 * writes resolve to the same instance. */
export function coreOpenerBase(): string {
  return import.meta.env.VITE_POSITION_OPENER_URL + '/api/position-opener';
}

/**
 * Resolve the opener API base URL for a rule, given the rule's (or its pair's)
 * `openerGroupId`. Grouped — a non-empty id AND the feed base env present —
 * targets the group's feed pod; otherwise the core base.
 */
export function resolveOpenerBase(openerGroupId?: string | null): string {
  const feedBase = import.meta.env.VITE_OPENER_FEED_URL as string | undefined;
  if (openerGroupId && feedBase) {
    return `${feedBase}/groups/${openerGroupId}/api/position-opener`;
  }
  return coreOpenerBase();
}

/**
 * The axios instance to issue an opener write against, given the rule's
 * `openerGroupId`. The instance carries the same bearer-token + 401-refresh
 * interceptors as the core `positionOpenerAxiosApi` (per-base instances are
 * cached in `axiosApi.ts`), so a grouped activation routed to a feed pod is
 * still authenticated and survives a silent token refresh. The core base
 * resolves back to `positionOpenerAxiosApi` unchanged.
 */
export function resolveOpenerApi(openerGroupId?: string | null): AxiosInstance {
  return resolveOpenerAxios(resolveOpenerBase(openerGroupId));
}

/**
 * Look up a rule's owning `openerGroupId` from the cached manual-confirmed
 * pairs list that `useManualConfirmedPairs` loads (each entry's `_id` is the
 * pairId and `adaptPair` already maps `openerGroupId`). A rule references its
 * pair via `pairId`; pass the rule's `pairId` here. Returns `null` when the
 * pair isn't found or has no group (→ core routing).
 */
export function openerGroupIdForPair(
  pairId: string | null | undefined,
  pairs: ReadonlyArray<{ _id: string; openerGroupId?: string | null }> | undefined
): string | null {
  if (!pairId || !pairs) return null;
  const pair = pairs.find(p => p._id === pairId);
  return pair?.openerGroupId ?? null;
}

export { positionOpenerAxiosApi };
